import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, ASSET_TYPE_LABELS, ASSET_STATUS_LABELS, STATUS_COLORS } from '../utils/api'
import { Asset } from '../types'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts'
import { FileText, Download, AlertTriangle, Package, Banknote, Building2, ChevronDown, Headphones, Clock, CheckCircle, XCircle, Inbox, Wrench, Sparkles } from 'lucide-react'
import ServiceDeskAiPanel from '../components/reports/ServiceDeskAiPanel'

const PIE_COLORS = ['#3b82f6','#10b981','#f59e0b','#6b7280','#ef4444','#8b5cf6']
const TICKET_PRIORITY_COLORS: Record<string,string> = { CRITICAL:'#ef4444', HIGH:'#f97316', MEDIUM:'#eab308', LOW:'#6b7280' }
const TICKET_PRIORITY_LABELS: Record<string,string> = { CRITICAL:'Critical', HIGH:'High', MEDIUM:'Medium', LOW:'Low' }
const TICKET_STATUS_COLORS: Record<string,string> = { OPEN:'#3b82f6', ASSIGNED:'#6366f1', IN_PROGRESS:'#8b5cf6', PENDING:'#f59e0b', RESOLVED:'#10b981', CLOSED:'#6b7280', CANCELLED:'#f87171' }
const TICKET_STATUS_LABELS: Record<string,string> = { OPEN:'เปิด', ASSIGNED:'มอบหมายแล้ว', IN_PROGRESS:'กำลังดำเนินการ', PENDING:'รอดำเนินการ', RESOLVED:'แก้ไขแล้ว', CLOSED:'ปิด', CANCELLED:'ยกเลิก' }
const fmt = (n: number) => `฿${n.toLocaleString()}`
const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('th-TH') : '-'
const fmtDateTime = (d?: string) => d ? new Date(d).toLocaleString('th-TH-u-ca-gregory', { dateStyle:'short', timeStyle:'short' }) : '-'

type ReportType = 'inventory'|'expiry'|'value'|'overdue'|'department'|'tickets'|'sla'|'ai-analysis'

export default function ReportsPage({ isAdmin = false }: { isAdmin?: boolean }) {
  const [activeReport, setActiveReport] = useState<ReportType>('tickets')
  const [selectedCompanyId, setSelectedCompanyId] = useState('')

  const { data: companies = [] } = useQuery<{id:string;name:string}[]>({
    queryKey: ['masterdata','companies'],
    queryFn: () => api.get('/masterdata/companies').then(r => r.data).catch(() => []),
  })

  const { data: assets = [] } = useQuery<Asset[]>({
    queryKey: ['all-assets-report', selectedCompanyId],
    queryFn: () => api.get(`/assets?limit=1000${selectedCompanyId ? `&companyId=${selectedCompanyId}` : ''}`).then(r => r.data.assets),
  })

  const { data: ticketStats } = useQuery({
    queryKey: ['ticket-stats-report', selectedCompanyId],
    queryFn: () => api.get(`/tickets/stats${selectedCompanyId ? `?companyId=${selectedCompanyId}` : ''}`).then(r => r.data).catch(() => null),
    enabled: activeReport === 'tickets' || activeReport === 'sla',
  })

  const { data: ticketReportData } = useQuery<any[]>({
    queryKey: ['tickets-report', selectedCompanyId],
    queryFn: () => api.get('/tickets', { params: { limit: 500, ...(selectedCompanyId && { companyId: selectedCompanyId }) } }).then(r => r.data.tickets || []),
    enabled: activeReport === 'tickets' || activeReport === 'sla',
  })

  const { data: maStats } = useQuery<any>({
    queryKey: ['ma-stats-report', selectedCompanyId],
    queryFn: () => api.get(`/maintenance/stats${selectedCompanyId ? `?companyId=${selectedCompanyId}` : ''}`).then(r => r.data).catch(() => null),
    enabled: activeReport === 'overdue',
  })

  const selectedCompanyName = companies.find(c => c.id === selectedCompanyId)?.name

  const handleExportCSV = (data: any[], filename: string) => {
    if (!data.length) return
    const headers = Object.keys(data[0])
    const csv = [headers.join(','), ...data.map(r => headers.map(h => `"${r[h]??''}"`).join(','))].join('\n')
    const blob = new Blob(['\uFEFF'+csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = filename+'.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  // ── Report 1: Asset Inventory ─────────────────────────────
  const byType = Object.entries(
    assets.reduce((acc, a) => { acc[a.type] = (acc[a.type]||0)+1; return acc }, {} as Record<string,number>)
  ).map(([type, count]) => ({ type: ASSET_TYPE_LABELS[type]||type, count }))

  const byStatus = Object.entries(
    assets.reduce((acc, a) => { acc[a.status] = (acc[a.status]||0)+1; return acc }, {} as Record<string,number>)
  ).map(([status, count]) => ({ status: ASSET_STATUS_LABELS[status]||status, count }))

  // ── Report 2: Expiry / Warranty ───────────────────────────
  const now = new Date()
  const in90 = new Date(now.getTime() + 90*24*60*60*1000)
  const in180 = new Date(now.getTime() + 180*24*60*60*1000)

  const warrantyExpiring90  = assets.filter(a => a.warrantyExpiry && new Date(a.warrantyExpiry) <= in90 && new Date(a.warrantyExpiry) >= now)
  const contractExpiring90  = assets.filter(a => a.contractEnd   && new Date(a.contractEnd)   <= in90 && new Date(a.contractEnd)   >= now)
  const warrantyExpiring180 = assets.filter(a => a.warrantyExpiry && new Date(a.warrantyExpiry) > in90 && new Date(a.warrantyExpiry) <= in180)
  const alreadyExpired      = assets.filter(a => a.warrantyExpiry && new Date(a.warrantyExpiry) < now)

  // ── Report 3: Asset Value ─────────────────────────────────
  const totalPurchase = assets.reduce((s, a) => s + (a.purchasePrice||0), 0)
  const totalCurrent  = assets.reduce((s, a) => s + (a.currentValue||0), 0)
  const totalDeprec   = assets.reduce((s, a) => s + (a.depreciation||0), 0)
  const byDeptValue   = Object.entries(
    assets.reduce((acc, a) => { const d = a.department||'ไม่ระบุ'; acc[d] = (acc[d]||0)+(a.purchasePrice||0); return acc }, {} as Record<string,number>)
  ).sort((a,b)=>b[1]-a[1]).slice(0,10).map(([dept,value])=>({dept, value}))

  // ── Report 4: Maintenance Overdue ────────────────────────
  const maOverdue = assets.filter(a => a.nextMaDate && new Date(a.nextMaDate) < now)

  // ── Report 5: By Department ──────────────────────────────
  const deptData = Object.entries(
    assets.reduce((acc, a) => {
      const d = a.department||'ไม่ระบุ'
      if (!acc[d]) acc[d] = { total:0, inUse:0, available:0, maintenance:0, value:0 }
      acc[d].total++
      if (a.status==='IN_USE') acc[d].inUse++
      if (a.status==='AVAILABLE') acc[d].available++
      if (a.status==='MAINTENANCE') acc[d].maintenance++
      acc[d].value += (a.purchasePrice||0)
      return acc
    }, {} as Record<string,{total:number;inUse:number;available:number;maintenance:number;value:number}>)
  ).sort((a,b)=>b[1].total-a[1].total)

  // ── Ticket report computed data ──────────────────────────────
  const tickets: any[] = ticketReportData || []
  const now2 = new Date()
  const ticketByCategory = Object.entries(
    tickets.reduce((acc: any, t: any) => {
      const cat = t.category?.name || 'ไม่ระบุ'
      acc[cat] = (acc[cat] || 0) + 1
      return acc
    }, {})
  ).sort((a: any, b: any) => b[1] - a[1]).slice(0, 10).map(([name, count]) => ({ name, count }))

  const slaTotal     = tickets.filter((t: any) => t.dueDate).length
  const slaBreach    = tickets.filter((t: any) => t.dueDate && (
    (t.status === 'RESOLVED' || t.status === 'CLOSED')
      ? new Date(t.resolvedAt || t.closedAt || 0) > new Date(t.dueDate)
      : new Date(t.dueDate) < now2
  )).length
  const slaMet       = slaTotal - slaBreach
  const slaCompliance = slaTotal > 0 ? Math.round((slaMet / slaTotal) * 100) : null

  const REPORTS: { key: ReportType; label: string; icon: React.ReactNode; count?: number }[] = [
    { key:'ai-analysis', label:'วิเคราะห์ด้วย AI',       icon:<Sparkles size={16}/> },
    { key:'tickets',    label:'ServiceDesk Tickets',   icon:<Headphones size={16}/>,        count: (ticketStats?.byStatus||[]).reduce((s:any,x:any)=>s+x._count._all,0) || undefined },
    { key:'sla',        label:'SLA Compliance',        icon:<Clock size={16}/>,         count: (ticketStats?.overdue||0) > 0 ? ticketStats?.overdue : undefined },
    { key:'inventory',  label:'Asset Inventory',       icon:<Package size={16}/>,       count: assets.length },
    { key:'expiry',     label:'ประกัน/สัญญาใกล้หมด', icon:<AlertTriangle size={16}/>,  count: warrantyExpiring90.length + contractExpiring90.length },
    { key:'value',      label:'มูลค่า Asset',          icon:<Banknote size={16}/> },
    { key:'overdue',    label:'รายงาน MA',             icon:<Wrench size={16}/>,        count: maOverdue.length },
    { key:'department', label:'รายงานตามแผนก',        icon:<FileText size={16}/> },
  ]

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-gray-900">รายงาน</h1>
          {selectedCompanyName && (
            <span className="flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded-full">
              <Building2 size={13}/> {selectedCompanyName}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Company Filter */}
          <div className="relative">
            <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
            <select
              value={selectedCompanyId}
              onChange={e => setSelectedCompanyId(e.target.value)}
              className="appearance-none pl-8 pr-8 py-1.5 text-sm border border-gray-200 rounded-xl bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-300 cursor-pointer"
            >
              <option value="">ทุกบริษัท</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
          </div>
          <button onClick={() => {
            const exportData = assets.map(a => ({'เลขเครื่อง':a.assetCode,'ชื่อ':a.name,'ประเภท':ASSET_TYPE_LABELS[a.type],'สถานะ':ASSET_STATUS_LABELS[a.status],'หน่วยงาน':a.department,'ผู้ใช้':a.assignedTo,'ราคา':a.purchasePrice,'หมดประกัน':fmtDate(a.warrantyExpiry),'MA ถัดไป':fmtDate(a.nextMaDate)}))
            handleExportCSV(exportData, `asset-inventory${selectedCompanyName?`-${selectedCompanyName}`:''}`)
          }} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-xl text-sm hover:bg-gray-50">
            <Download size={16}/> Export ทั้งหมด
          </button>
        </div>
      </div>

      {/* Report Tabs */}
      <div className="flex gap-2 flex-wrap">
        {REPORTS.map(r => (
          <button key={r.key} onClick={() => setActiveReport(r.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all border ${activeReport===r.key?'bg-blue-600 text-white border-blue-600':'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>
            {r.icon} {r.label}
            {r.count != null && r.count > 0 && <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${activeReport===r.key?'bg-blue-400 text-white':'bg-red-100 text-red-700'}`}>{r.count}</span>}
          </button>
        ))}
      </div>

      {/* ── AI Analysis (ServiceDesk) ── */}
      {activeReport === 'ai-analysis' && <ServiceDeskAiPanel isAdmin={isAdmin} />}

      {/* ── Ticket Report ── */}
      {activeReport === 'tickets' && (
        <div className="space-y-5">
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {(ticketStats?.byStatus || []).map((s: any) => (
              <div key={s.status} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                <div className="w-2 h-2 rounded-full mb-2" style={{ background: TICKET_STATUS_COLORS[s.status] || '#6b7280' }}/>
                <p className="text-xs text-gray-500 mb-0.5">{TICKET_STATUS_LABELS[s.status] || s.status}</p>
                <p className="text-2xl font-bold text-gray-900">{s._count._all}</p>
              </div>
            ))}
            {ticketStats && (
              <div className="bg-red-50 rounded-xl p-4 border border-red-200 shadow-sm">
                <div className="w-2 h-2 rounded-full mb-2 bg-red-500"/>
                <p className="text-xs text-red-600 mb-0.5">เกิน SLA</p>
                <p className="text-2xl font-bold text-red-700">{ticketStats.overdue || 0}</p>
              </div>
            )}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {(ticketStats?.byStatus || []).length > 0 && (
              <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <h3 className="font-semibold text-gray-700 mb-4">Tickets ตามสถานะ</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={(ticketStats.byStatus||[]).map((s:any)=>({ name: TICKET_STATUS_LABELS[s.status]||s.status, value: s._count._all, key: s.status }))} margin={{top:5,right:10,left:-20,bottom:5}}>
                    <XAxis dataKey="name" tick={{fontSize:10}}/><YAxis tick={{fontSize:11}} allowDecimals={false}/>
                    <Tooltip/>
                    <Bar dataKey="value" radius={[4,4,0,0]} name="จำนวน">
                      {(ticketStats.byStatus||[]).map((s:any,i:number) => <Cell key={i} fill={TICKET_STATUS_COLORS[s.status]||'#6b7280'}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {(ticketStats?.byPriority || []).length > 0 && (
              <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <h3 className="font-semibold text-gray-700 mb-4">Tickets ตาม Priority</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={(ticketStats.byPriority||[]).map((p:any)=>({ name: TICKET_PRIORITY_LABELS[p.priority]||p.priority, value: p._count._all }))}
                      cx="50%" cy="50%" outerRadius={70} dataKey="value"
                      label={({name,value})=>`${name}: ${value}`} labelLine={false}>
                      {(ticketStats.byPriority||[]).map((p:any,i:number) => <Cell key={i} fill={TICKET_PRIORITY_COLORS[p.priority]||PIE_COLORS[i]}/>)}
                    </Pie>
                    <Tooltip/><Legend/>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            {ticketByCategory.length > 0 && (
              <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm lg:col-span-2">
                <h3 className="font-semibold text-gray-700 mb-4">Tickets ตามหมวดหมู่ (Top 10)</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={ticketByCategory} margin={{top:5,right:10,left:-20,bottom:5}}>
                    <XAxis dataKey="name" tick={{fontSize:11}}/><YAxis tick={{fontSize:11}} allowDecimals={false}/>
                    <Tooltip/><Bar dataKey="count" fill="#8b5cf6" radius={[4,4,0,0]} name="จำนวน"/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Ticket list table */}
          {tickets.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-50">
                <h3 className="font-semibold text-gray-700">รายการ Tickets ({tickets.length})</h3>
                <button onClick={() => handleExportCSV(tickets.map((t:any) => ({
                  'เลขที่': `#${String(t.ticketNumber).padStart(4,'0')}`,
                  'หัวข้อ': t.title,
                  'ประเภท': t.type === 'INCIDENT' ? 'Incident' : 'Service Request',
                  'ความสำคัญ': TICKET_PRIORITY_LABELS[t.priority] || t.priority,
                  'สถานะ': TICKET_STATUS_LABELS[t.status] || t.status,
                  'หมวดหมู่': t.category?.name || '',
                  'ผู้แจ้ง': t.requester?.displayName || t.requester?.username || '',
                  'ผู้รับผิดชอบ': t.assignee?.displayName || t.assignee?.username || '',
                  'บริษัท': t.company?.name || '',
                  'วันที่สร้าง': fmtDateTime(t.createdAt),
                  'Due Date (SLA)': fmtDateTime(t.dueDate),
                  'วันที่แก้ไข': fmtDateTime(t.resolvedAt),
                  'วันที่ปิด': fmtDateTime(t.closedAt),
                })), `tickets-report${selectedCompanyName?`-${selectedCompanyName}`:''}`)}>
                  <Download size={14}/> Export CSV
                </button>
              </div>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">#</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">หัวข้อ</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Priority</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">สถานะ</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">หมวดหมู่</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">ผู้รับผิดชอบ</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Due Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {tickets.map((t: any) => {
                      const isOverdue = t.dueDate && !['RESOLVED','CLOSED','CANCELLED'].includes(t.status) && new Date(t.dueDate) < now2
                      return (
                        <tr key={t.id} className={`hover:bg-gray-50 ${isOverdue ? 'bg-red-50/30' : ''}`}>
                          <td className="px-4 py-2.5 font-mono text-xs text-gray-500">#{String(t.ticketNumber).padStart(4,'0')}</td>
                          <td className="px-4 py-2.5 max-w-xs"><p className="truncate font-medium text-gray-900">{t.title}</p></td>
                          <td className="px-4 py-2.5">
                            <span className="text-xs px-1.5 py-0.5 rounded font-medium" style={{ background: TICKET_PRIORITY_COLORS[t.priority]+'22', color: TICKET_PRIORITY_COLORS[t.priority] }}>
                              {TICKET_PRIORITY_LABELS[t.priority] || t.priority}
                            </span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="text-xs px-1.5 py-0.5 rounded font-medium" style={{ background: TICKET_STATUS_COLORS[t.status]+'22', color: TICKET_STATUS_COLORS[t.status] }}>
                              {TICKET_STATUS_LABELS[t.status] || t.status}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-gray-500">{t.category?.name || '—'}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-600">{t.assignee?.displayName || t.assignee?.username || <span className="text-gray-300">—</span>}</td>
                          <td className={`px-4 py-2.5 text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                            {t.dueDate ? fmtDateTime(t.dueDate) : '—'}
                            {isOverdue && <span className="ml-1 text-red-500">⚠</span>}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {tickets.length === 0 && !ticketStats && (
            <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
              <Headphones size={32} className="mx-auto text-gray-200 mb-3"/>
              <p className="text-gray-400">ยังไม่มีข้อมูล Ticket</p>
            </div>
          )}
        </div>
      )}

      {/* ── SLA Compliance Report ── */}
      {activeReport === 'sla' && (
        <div className="space-y-5">
          {/* SLA Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2 mb-2"><Inbox size={16} className="text-blue-500"/><p className="text-sm text-gray-500">Tickets มี SLA</p></div>
              <p className="text-3xl font-bold text-gray-900">{slaTotal}</p>
            </div>
            <div className="bg-green-50 rounded-xl p-5 border border-green-200 shadow-sm">
              <div className="flex items-center gap-2 mb-2"><CheckCircle size={16} className="text-green-600"/><p className="text-sm text-green-700">ผ่าน SLA</p></div>
              <p className="text-3xl font-bold text-green-900">{slaMet}</p>
            </div>
            <div className="bg-red-50 rounded-xl p-5 border border-red-200 shadow-sm">
              <div className="flex items-center gap-2 mb-2"><XCircle size={16} className="text-red-600"/><p className="text-sm text-red-700">เกิน SLA</p></div>
              <p className="text-3xl font-bold text-red-900">{slaBreach}</p>
            </div>
            <div className={`rounded-xl p-5 border shadow-sm ${slaCompliance === null ? 'bg-white border-gray-100' : slaCompliance >= 90 ? 'bg-green-50 border-green-200' : slaCompliance >= 70 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center gap-2 mb-2"><Clock size={16} className={slaCompliance === null ? 'text-gray-400' : slaCompliance >= 90 ? 'text-green-600' : slaCompliance >= 70 ? 'text-amber-600' : 'text-red-600'}/><p className="text-sm text-gray-600">SLA Compliance</p></div>
              <p className={`text-3xl font-bold ${slaCompliance === null ? 'text-gray-400' : slaCompliance >= 90 ? 'text-green-900' : slaCompliance >= 70 ? 'text-amber-900' : 'text-red-900'}`}>
                {slaCompliance !== null ? `${slaCompliance}%` : '—'}
              </p>
            </div>
          </div>

          {/* SLA by Priority */}
          {tickets.length > 0 && (
            <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
              <h3 className="font-semibold text-gray-700 mb-4">SLA ตาม Priority</h3>
              <table className="w-full text-sm">
                <thead className="bg-gray-50"><tr>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Priority</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold text-gray-500">ทั้งหมด</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold text-gray-500">ผ่าน SLA</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold text-gray-500">เกิน SLA</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold text-gray-500">Compliance</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {['CRITICAL','HIGH','MEDIUM','LOW'].map(priority => {
                    const priorityTickets = tickets.filter((t:any) => t.priority === priority && t.dueDate)
                    const breach = priorityTickets.filter((t:any) => (
                      (t.status === 'RESOLVED' || t.status === 'CLOSED')
                        ? new Date(t.resolvedAt || t.closedAt || 0) > new Date(t.dueDate)
                        : new Date(t.dueDate) < now2
                    )).length
                    const met = priorityTickets.length - breach
                    const pct = priorityTickets.length > 0 ? Math.round((met / priorityTickets.length) * 100) : null
                    if (priorityTickets.length === 0) return null
                    return (
                      <tr key={priority} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className="text-xs font-medium px-2 py-0.5 rounded" style={{ background: TICKET_PRIORITY_COLORS[priority]+'22', color: TICKET_PRIORITY_COLORS[priority] }}>
                            {TICKET_PRIORITY_LABELS[priority]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center font-semibold text-gray-900">{priorityTickets.length}</td>
                        <td className="px-4 py-3 text-center text-green-600">{met}</td>
                        <td className="px-4 py-3 text-center text-red-600">{breach}</td>
                        <td className="px-4 py-3 text-center">
                          {pct !== null && (
                            <span className={`text-sm font-bold ${pct >= 90 ? 'text-green-600' : pct >= 70 ? 'text-amber-600' : 'text-red-600'}`}>{pct}%</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Export breached tickets */}
          {tickets.filter((t:any) => t.dueDate && (
            (t.status==='RESOLVED'||t.status==='CLOSED') ? new Date(t.resolvedAt||t.closedAt||0) > new Date(t.dueDate) : new Date(t.dueDate) < now2
          )).length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-50">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                  <AlertTriangle size={16} className="text-red-500"/> Tickets เกิน SLA ({slaBreach})
                </h3>
                <button onClick={() => handleExportCSV(
                  tickets.filter((t:any) => t.dueDate && (
                    (t.status==='RESOLVED'||t.status==='CLOSED') ? new Date(t.resolvedAt||t.closedAt||0) > new Date(t.dueDate) : new Date(t.dueDate) < now2
                  )).map((t:any) => ({
                    'เลขที่': `#${String(t.ticketNumber).padStart(4,'0')}`,
                    'หัวข้อ': t.title,
                    'Priority': TICKET_PRIORITY_LABELS[t.priority]||t.priority,
                    'สถานะ': TICKET_STATUS_LABELS[t.status]||t.status,
                    'Due Date': fmtDateTime(t.dueDate),
                    'วันที่แก้ไข': fmtDateTime(t.resolvedAt),
                    'ผู้รับผิดชอบ': t.assignee?.displayName||t.assignee?.username||'',
                  })), 'sla-breach')}>
                  <Download size={14}/> Export CSV
                </button>
              </div>
              <div className="overflow-x-auto max-h-72">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0"><tr>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">#</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">หัวข้อ</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Priority</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">สถานะ</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Due Date</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">ผู้รับผิดชอบ</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {tickets.filter((t:any) => t.dueDate && (
                      (t.status==='RESOLVED'||t.status==='CLOSED') ? new Date(t.resolvedAt||t.closedAt||0) > new Date(t.dueDate) : new Date(t.dueDate) < now2
                    )).map((t:any) => (
                      <tr key={t.id} className="hover:bg-red-50/30">
                        <td className="px-4 py-2.5 font-mono text-xs text-gray-500">#{String(t.ticketNumber).padStart(4,'0')}</td>
                        <td className="px-4 py-2.5 max-w-xs"><p className="truncate font-medium text-gray-900">{t.title}</p></td>
                        <td className="px-4 py-2.5">
                          <span className="text-xs font-medium px-1.5 py-0.5 rounded" style={{ background: TICKET_PRIORITY_COLORS[t.priority]+'22', color: TICKET_PRIORITY_COLORS[t.priority] }}>
                            {TICKET_PRIORITY_LABELS[t.priority]||t.priority}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="text-xs font-medium px-1.5 py-0.5 rounded" style={{ background: TICKET_STATUS_COLORS[t.status]+'22', color: TICKET_STATUS_COLORS[t.status] }}>
                            {TICKET_STATUS_LABELS[t.status]||t.status}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-red-600 font-medium">{fmtDateTime(t.dueDate)}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-600">{t.assignee?.displayName||t.assignee?.username||'—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Avg Resolution Time + Agent Performance */}
          {ticketStats && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <Clock size={15} className="text-blue-500"/> เวลาแก้ไขเฉลี่ย (30 วัน)
                </h3>
                <p className="text-4xl font-bold text-gray-900">
                  {ticketStats.avgResolutionMinutes
                    ? `${(ticketStats.avgResolutionMinutes / 60).toFixed(1)} ชม.`
                    : <span className="text-gray-300">—</span>}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {ticketStats.avgResolutionMinutes ? `${ticketStats.avgResolutionMinutes} นาที` : 'ยังไม่มีข้อมูล'}
                </p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <CheckCircle size={15} className="text-green-500"/> Agent Performance (30 วัน)
                </h3>
                {ticketStats.agentPerformance?.length > 0 ? (
                  <div className="space-y-2">
                    {[...ticketStats.agentPerformance].sort((a: any, b: any) => b.count - a.count).slice(0, 8).map((agent: any) => (
                      <div key={agent.assigneeId} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {(agent.assigneeName || '?')[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-xs font-medium text-gray-700 truncate">{agent.assigneeName}</span>
                            <span className="text-xs text-gray-500 ml-2">{agent.count}</span>
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-full">
                            <div className="h-1.5 bg-blue-500 rounded-full"
                              style={{ width: `${Math.round((agent.count / Math.max(...ticketStats.agentPerformance.map((a: any) => a.count))) * 100)}%` }}/>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">ยังไม่มีข้อมูล</p>
                )}
              </div>
            </div>
          )}

          {slaTotal === 0 && (
            <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
              <Clock size={32} className="mx-auto text-gray-200 mb-3"/>
              <p className="text-gray-400">ยังไม่มีข้อมูล SLA</p>
            </div>
          )}
        </div>
      )}

      {/* ── Report 1: Inventory ── */}
      {activeReport === 'inventory' && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {byStatus.map((s,i) => <div key={i} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm"><p className="text-xs text-gray-500 mb-1">{s.status}</p><p className="text-2xl font-bold text-gray-900">{s.count}</p></div>)}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
              <h3 className="font-semibold text-gray-700 mb-4">ตามประเภท</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={byType}><XAxis dataKey="type" tick={{fontSize:11}}/><YAxis tick={{fontSize:11}} allowDecimals={false}/><Tooltip/><Bar dataKey="count" fill="#3b82f6" radius={[4,4,0,0]}/></BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
              <h3 className="font-semibold text-gray-700 mb-4">ตามสถานะ</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart><Pie data={byStatus} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={70} label={({status,count})=>`${status}: ${count}`} labelLine={false}>{byStatus.map((_,i)=><Cell key={i} fill={PIE_COLORS[i]}/>)}</Pie><Tooltip/><Legend/></PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-50">
              <h3 className="font-semibold text-gray-700">รายการ Assets ทั้งหมด ({assets.length})</h3>
              <button onClick={() => handleExportCSV(assets.map(a=>({'เลขเครื่อง':a.assetCode,'ชื่อ':a.name,'ประเภท':ASSET_TYPE_LABELS[a.type],'สถานะ':ASSET_STATUS_LABELS[a.status],'หน่วยงาน':a.department,'ผู้ใช้':a.assignedTo,'ราคา':a.purchasePrice})), 'inventory')} className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline"><Download size={14}/>Export</button>
            </div>
            <div className="overflow-x-auto max-h-80">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0"><tr><th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">ชื่อ / รหัส</th><th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">ประเภท</th><th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">สถานะ</th><th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">หน่วยงาน</th><th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">ราคา</th></tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {assets.map(a => (
                    <tr key={a.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3"><div className="font-medium text-gray-900">{a.name}</div>{a.assetCode&&<div className="text-xs font-mono text-blue-600">{a.assetCode}</div>}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{ASSET_TYPE_LABELS[a.type]}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[a.status]}`}>{ASSET_STATUS_LABELS[a.status]}</span></td>
                      <td className="px-4 py-3 text-xs text-gray-500">{a.department||'-'}</td>
                      <td className="px-4 py-3 text-right text-xs text-gray-600">{a.purchasePrice?fmt(a.purchasePrice):'-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Report 2: Expiry ── */}
      {activeReport === 'expiry' && (
        <div className="space-y-4">
          {[
            {title:'🔴 หมดอายุแล้ว', items: alreadyExpired, color:'red'},
            {title:'🟠 หมดใน 90 วัน — ประกัน', items: warrantyExpiring90, color:'orange'},
            {title:'🟠 หมดใน 90 วัน — สัญญาเช่า', items: contractExpiring90, color:'orange'},
            {title:'🟡 หมดใน 90-180 วัน — ประกัน', items: warrantyExpiring180, color:'yellow'},
          ].map(group => group.items.length > 0 && (
            <div key={group.title} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-50">
                <h3 className="font-semibold text-gray-700">{group.title} ({group.items.length})</h3>
                <button onClick={() => handleExportCSV(group.items.map(a=>({'ชื่อ':a.name,'รหัส':a.assetCode,'หน่วยงาน':a.department,'ผู้ใช้':a.assignedTo,'หมดประกัน':fmtDate(a.warrantyExpiry),'หมดสัญญา':fmtDate(a.contractEnd)})), 'expiry')} className="flex items-center gap-1 text-sm text-blue-600 hover:underline"><Download size={12}/>Export</button>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50"><tr><th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">ชื่อ</th><th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">หน่วยงาน</th><th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">ผู้ใช้</th><th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">หมดประกัน</th><th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">หมดสัญญา</th></tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {group.items.map(a => <tr key={a.id} className="hover:bg-gray-50"><td className="px-4 py-2.5 font-medium text-gray-900">{a.name}{a.assetCode&&<span className="ml-2 text-xs font-mono text-blue-600">{a.assetCode}</span>}</td><td className="px-4 py-2.5 text-xs text-gray-500">{a.department||'-'}</td><td className="px-4 py-2.5 text-xs text-gray-500">{a.assignedTo||'-'}</td><td className="px-4 py-2.5 text-xs text-red-600">{fmtDate(a.warrantyExpiry)}</td><td className="px-4 py-2.5 text-xs text-amber-600">{fmtDate(a.contractEnd)}</td></tr>)}
                </tbody>
              </table>
            </div>
          ))}
          {warrantyExpiring90.length===0 && contractExpiring90.length===0 && alreadyExpired.length===0 && (
            <div className="bg-white rounded-xl p-12 text-center border border-gray-100"><AlertTriangle size={32} className="mx-auto text-gray-200 mb-3"/><p className="text-gray-400">ไม่มีประกัน/สัญญาที่ใกล้หมด</p></div>
          )}
        </div>
      )}

      {/* ── Report 3: Value ── */}
      {activeReport === 'value' && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 border border-blue-200"><p className="text-sm text-blue-700 font-medium mb-1">มูลค่ารวม (ราคาซื้อ)</p><p className="text-2xl font-bold text-blue-900">{fmt(totalPurchase)}</p></div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5 border border-green-200"><p className="text-sm text-green-700 font-medium mb-1">มูลค่าปัจจุบันรวม</p><p className="text-2xl font-bold text-green-900">{fmt(totalCurrent)}</p></div>
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-5 border border-amber-200"><p className="text-sm text-amber-700 font-medium mb-1">ค่าเสื่อมราคารวม</p><p className="text-2xl font-bold text-amber-900">{fmt(totalDeprec)}</p></div>
          </div>
          {byDeptValue.length > 0 && (
            <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
              <h3 className="font-semibold text-gray-700 mb-4">มูลค่า Asset ตามหน่วยงาน (Top 10)</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={byDeptValue} margin={{top:5,right:10,left:20,bottom:5}}><XAxis dataKey="dept" tick={{fontSize:11}}/><YAxis tick={{fontSize:11}} tickFormatter={v=>v>=1000?`฿${(v/1000).toFixed(0)}K`:`฿${v}`}/><Tooltip formatter={(v:any)=>fmt(v)}/><Bar dataKey="value" fill="#3b82f6" radius={[4,4,0,0]} name="มูลค่า"/></BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-50">
              <h3 className="font-semibold text-gray-700">ข้อมูลการเงินรายชิ้น</h3>
              <button onClick={() => handleExportCSV(assets.filter(a=>a.purchasePrice).map(a=>({'ชื่อ':a.name,'รหัส':a.assetCode,'ราคาซื้อ':a.purchasePrice,'มูลค่าปัจจุบัน':a.currentValue,'ค่าเสื่อม':a.depreciation,'net book':a.netBook,'cost center':a.costCenter})), 'asset-value')} className="flex items-center gap-1 text-sm text-blue-600 hover:underline"><Download size={12}/>Export</button>
            </div>
            <div className="overflow-x-auto max-h-72">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0"><tr><th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">ชื่อ</th><th className="text-right px-4 py-2 text-xs font-semibold text-gray-500">ราคาซื้อ</th><th className="text-right px-4 py-2 text-xs font-semibold text-gray-500">มูลค่าปัจจุบัน</th><th className="text-right px-4 py-2 text-xs font-semibold text-gray-500">ค่าเสื่อม</th></tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {assets.filter(a=>a.purchasePrice).sort((a,b)=>(b.purchasePrice||0)-(a.purchasePrice||0)).map(a=>(
                    <tr key={a.id} className="hover:bg-gray-50"><td className="px-4 py-2.5 font-medium text-gray-900">{a.name}{a.assetCode&&<span className="ml-2 text-xs font-mono text-blue-600">{a.assetCode}</span>}</td><td className="px-4 py-2.5 text-right text-gray-700">{fmt(a.purchasePrice!)}</td><td className="px-4 py-2.5 text-right text-gray-500">{a.currentValue?fmt(a.currentValue):'-'}</td><td className="px-4 py-2.5 text-right text-gray-500">{a.depreciation?fmt(a.depreciation):'-'}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Report 4: MA Report ── */}
      {activeReport === 'overdue' && (
        <div className="space-y-5">
          {!maStats && (
            <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
              <Wrench size={32} className="mx-auto text-gray-200 mb-3 animate-pulse"/>
              <p className="text-gray-400">กำลังโหลดข้อมูล MA...</p>
            </div>
          )}
          {maStats && (
            <>
              {/* Compliance summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className={`rounded-xl p-5 border shadow-sm ${maStats.compliancePercent >= 90 ? 'bg-green-50 border-green-200' : maStats.compliancePercent >= 60 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center gap-2 mb-2"><Wrench size={16} className={maStats.compliancePercent >= 90 ? 'text-green-600' : maStats.compliancePercent >= 60 ? 'text-amber-600' : 'text-red-600'}/><p className="text-sm text-gray-600">Asset ที่มี MA</p></div>
                  <p className={`text-3xl font-bold ${maStats.compliancePercent >= 90 ? 'text-green-900' : maStats.compliancePercent >= 60 ? 'text-amber-900' : 'text-red-900'}`}>{maStats.compliancePercent}%</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                  <div className="flex items-center gap-2 mb-2"><CheckCircle size={16} className="text-green-600"/><p className="text-sm text-gray-500">มี MA แล้ว</p></div>
                  <p className="text-3xl font-bold text-gray-900">{maStats.assetsWithMA}</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                  <div className="flex items-center gap-2 mb-2"><XCircle size={16} className="text-gray-400"/><p className="text-sm text-gray-500">ยังไม่มี MA</p></div>
                  <p className="text-3xl font-bold text-gray-900">{maStats.assetsWithoutMA}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 border border-blue-200">
                  <div className="flex items-center gap-2 mb-2"><Banknote size={16} className="text-blue-700"/><p className="text-sm text-blue-700 font-medium">ค่าใช้จ่าย MA รวม</p></div>
                  <p className="text-2xl font-bold text-blue-900">{fmt(maStats.totalCostAllTime)}</p>
                  <p className="text-xs text-blue-600 mt-0.5">{maStats.totalMaintenanceCount} ครั้ง</p>
                </div>
              </div>

              {/* Due buckets */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-red-50 border border-red-200 rounded-xl p-4"><p className="text-sm text-red-700 font-medium">เกินกำหนด</p><p className="text-3xl font-bold text-red-900 mt-1">{maStats.overdue.length}</p></div>
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4"><p className="text-sm text-orange-700 font-medium">ภายใน 7 วัน</p><p className="text-3xl font-bold text-orange-900 mt-1">{maStats.due7.length}</p></div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4"><p className="text-sm text-amber-700 font-medium">ภายใน 30 วัน</p><p className="text-3xl font-bold text-amber-900 mt-1">{maStats.due30.length}</p></div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4"><p className="text-sm text-blue-700 font-medium">ภายใน 90 วัน</p><p className="text-3xl font-bold text-blue-900 mt-1">{maStats.due90.length}</p></div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {(maStats.byDepartment||[]).length > 0 && (
                  <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                    <h3 className="font-semibold text-gray-700 mb-4">MA เกิน/ใกล้ครบกำหนด ตามหน่วยงาน (Top 8)</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={maStats.byDepartment.slice(0,8)} margin={{top:5,right:10,left:-20,bottom:5}}>
                        <XAxis dataKey="department" tick={{fontSize:10}}/><YAxis tick={{fontSize:11}} allowDecimals={false}/>
                        <Tooltip/><Legend/>
                        <Bar dataKey="overdue" stackId="a" fill="#ef4444" name="เกินกำหนด"/>
                        <Bar dataKey="upcoming" stackId="a" fill="#f59e0b" name="ใกล้ครบกำหนด" radius={[4,4,0,0]}/>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {(maStats.byType||[]).length > 0 && (
                  <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                    <h3 className="font-semibold text-gray-700 mb-4">MA ตามประเภท (12 เดือนล่าสุด)</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={maStats.byType.map((t:any)=>({name:t.type, value:t.count}))} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({name,value})=>`${name}: ${value}`} labelLine={false}>
                          {maStats.byType.map((_:any,i:number) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]}/>)}
                        </Pie>
                        <Tooltip/><Legend/>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {(maStats.byVendor||[]).length > 0 && (
                  <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                    <h3 className="font-semibold text-gray-700 mb-4">MA ตาม Vendor (Top 8, 12 เดือนล่าสุด)</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={maStats.byVendor.slice(0,8)} margin={{top:5,right:10,left:-20,bottom:5}}>
                        <XAxis dataKey="vendor" tick={{fontSize:10}}/><YAxis tick={{fontSize:11}} allowDecimals={false}/>
                        <Tooltip/><Bar dataKey="count" fill="#8b5cf6" radius={[4,4,0,0]} name="จำนวนครั้ง MA"/>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {(maStats.monthlyTrend||[]).length > 0 && (
                  <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                    <h3 className="font-semibold text-gray-700 mb-4">แนวโน้ม MA รายเดือน (12 เดือนล่าสุด)</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={maStats.monthlyTrend} margin={{top:5,right:10,left:-20,bottom:5}}>
                        <XAxis dataKey="month" tick={{fontSize:10}}/><YAxis tick={{fontSize:11}} allowDecimals={false}/>
                        <Tooltip/><Bar dataKey="count" fill="#3b82f6" radius={[4,4,0,0]} name="จำนวนครั้ง MA"/>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Due buckets tables */}
              {[
                {title:'🔴 เกินกำหนด MA', items: maStats.overdue, color: 'text-red-600'},
                {title:'🟠 ครบกำหนดภายใน 7 วัน', items: maStats.due7, color: 'text-orange-600'},
                {title:'🟡 ครบกำหนดภายใน 30 วัน', items: maStats.due30, color: 'text-amber-600'},
                {title:'🔵 ครบกำหนดภายใน 90 วัน', items: maStats.due90, color: 'text-blue-600'},
              ].map(g => g.items.length > 0 && (
                <div key={g.title} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="p-4 border-b border-gray-50 flex items-center justify-between">
                    <h3 className="font-semibold text-gray-700">{g.title} ({g.items.length})</h3>
                    <button onClick={()=>handleExportCSV(g.items.map((a:any)=>({'ชื่อ':a.name,'รหัส':a.assetCode,'หน่วยงาน':a.department,'ผู้ใช้':a.assignedTo,'Vendor':a.vendorName,'กำหนด MA':fmtDate(a.nextMaDate)})),'ma-report')} className="flex items-center gap-1 text-sm text-blue-600 hover:underline"><Download size={12}/>Export</button>
                  </div>
                  <div className="overflow-x-auto max-h-72">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0"><tr>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">ชื่อ</th>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">หน่วยงาน</th>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">ผู้ใช้</th>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Vendor</th>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">กำหนด MA</th>
                      </tr></thead>
                      <tbody className="divide-y divide-gray-50">
                        {g.items.map((a:any) => (
                          <tr key={a.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2.5 font-medium text-gray-900">{a.name}{a.assetCode && <span className="ml-2 text-xs font-mono text-blue-600">{a.assetCode}</span>}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-500">{a.department||'-'}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-500">{a.assignedTo||'-'}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-500">{a.vendorName||'-'}</td>
                            <td className={`px-4 py-2.5 text-xs font-medium ${g.color}`}>{fmtDate(a.nextMaDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              {/* Recent MA activity */}
              {(maStats.recentHistory||[]).length > 0 && (
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between p-4 border-b border-gray-50">
                    <h3 className="font-semibold text-gray-700">ประวัติ MA ล่าสุด</h3>
                    <button onClick={()=>handleExportCSV(maStats.recentHistory.map((r:any)=>({'วันที่':fmtDate(r.date),'Asset':r.assetName,'รหัส':r.assetCode,'ประเภท':r.type,'ผู้ดำเนินการ':r.performedBy,'Vendor':r.vendorName,'ค่าใช้จ่าย':r.cost,'รายละเอียด':r.description})),'ma-history')} className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline"><Download size={14}/>Export</button>
                  </div>
                  <div className="overflow-x-auto max-h-80">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0"><tr>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">วันที่</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Asset</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">ประเภท</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Vendor</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500">ค่าใช้จ่าย</th>
                      </tr></thead>
                      <tbody className="divide-y divide-gray-50">
                        {maStats.recentHistory.map((r:any) => (
                          <tr key={r.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2.5 text-xs text-gray-500">{fmtDate(r.date)}</td>
                            <td className="px-4 py-2.5"><div className="font-medium text-gray-900">{r.assetName}</div>{r.assetCode && <div className="text-xs font-mono text-blue-600">{r.assetCode}</div>}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-500">{r.type}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-500">{r.vendorName||'-'}</td>
                            <td className="px-4 py-2.5 text-right text-xs text-gray-600">{r.cost?fmt(r.cost):'-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {maStats.overdue.length===0 && maStats.due7.length===0 && maStats.due30.length===0 && maStats.due90.length===0 && (
                <div className="bg-white rounded-xl p-12 text-center border border-gray-100"><Package size={32} className="mx-auto text-gray-200 mb-3"/><p className="text-gray-400">ไม่มี MA ที่เกินกำหนดหรือใกล้ครบกำหนด</p></div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Report 5: By Department ── */}
      {activeReport === 'department' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-gray-50">
            <h3 className="font-semibold text-gray-700">สรุป Asset ตามหน่วยงาน</h3>
            <button onClick={()=>handleExportCSV(deptData.map(([d,v])=>({'หน่วยงาน':d,'ทั้งหมด':v.total,'ใช้งาน':v.inUse,'ว่าง':v.available,'ซ่อม':v.maintenance,'มูลค่ารวม':v.value})),'by-department')} className="flex items-center gap-1 text-sm text-blue-600"><Download size={12}/>Export</button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr><th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">หน่วยงาน</th><th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">ทั้งหมด</th><th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">ใช้งาน</th><th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">ว่าง</th><th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">ซ่อม</th><th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">มูลค่า</th></tr></thead>
            <tbody className="divide-y divide-gray-50">
              {deptData.map(([dept,v]) => (
                <tr key={dept} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{dept}</td>
                  <td className="px-4 py-3 text-center"><span className="font-bold text-gray-900">{v.total}</span></td>
                  <td className="px-4 py-3 text-center"><span className="text-blue-600">{v.inUse}</span></td>
                  <td className="px-4 py-3 text-center"><span className="text-green-600">{v.available}</span></td>
                  <td className="px-4 py-3 text-center"><span className="text-amber-600">{v.maintenance}</span></td>
                  <td className="px-4 py-3 text-right text-gray-600">{v.value>0?fmt(v.value):'-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
